/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Typography,
  Box,
  CircularProgress,
  Button,
  Dialog,
  DialogContent,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  Grow,
} from '@mui/material';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Sector } from 'recharts';

import CleanupModal from './CleanupModal';

const API_URL = process.env.REACT_APP_API_URL || 'https://aqa.wtdev.dev';

interface RawReport {
  date: string;
  country: string;
  suites: {
    suite: string;
    platforms: {
      platform: string;
      tests: {
        testName: string;
        status: 'passed' | 'failed' | 'skipped';
        duration: number;
        error?: {
          message: string;
          pathToScreenshot?: string;
        };
        annotations?: Record<string, string>;
      }[];
    }[];
  }[];
}

interface ChartItem {
  name: string;
  value: number;
  fill: string;
}

const getColor = (status: string) => {
  if (status === 'failed') return '#FF4C4C';
  if (status === 'skipped') return '#FFBB28';
  return '#00C49F';
};

const Dashboard = () => {
  const [data, setData] = useState<RawReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [renderingCharts, setRenderingCharts] = useState(false);
  const [drilledSuite, setDrilledSuite] = useState<string | null>(null);
  const [drilledCountry, setDrilledCountry] = useState<string | null>(null);
  const [drilledPlatform, setDrilledPlatform] = useState<string | null>(null);
  const [drilledDate, setDrilledDate] = useState<string | null>(null);
  const [openImage, setOpenImage] = useState<string | null>(null);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedDevice, setSelectedDevice] = useState('');
  const [filterActive, setFilterActive] = useState(false);
  const [expandedTest, setExpandedTest] = useState<string | null>(null);
  const [expandedCard, setExpandedCard] = useState<number | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    fetch(`${API_URL}/api/results`)
      .then((res) => res.json())
      .then((json) => {
        setData(json);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load test results:', err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (
      (drilledSuite && drilledCountry && drilledDate && !drilledPlatform) ||
      (drilledSuite && drilledCountry && drilledDate && drilledPlatform) ||
      (!drilledSuite && !drilledCountry && !drilledPlatform && !filterActive)
    ) {
      const timeout = setTimeout(() => setRenderingCharts(false), 300);
      return () => clearTimeout(timeout);
    }
  }, [drilledSuite, drilledCountry, drilledDate, drilledPlatform, filterActive]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#0f1117',
          color: '#f1f1f1',
        }}
      >
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  const uniqueDates = [...new Set(data.map((d) => d.date))]
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
    .slice(0, 3);
  const suiteNames = Array.from(new Set(data.flatMap((d) => d.suites.map((s) => s.suite))));
  const allCountries = Array.from(new Set(data.map((d) => d.country)));
  const allDevices = Array.from(
    new Set(data.flatMap((d) => d.suites.flatMap((s) => s.platforms.map((p) => p.platform)))),
  );

  const filteredTests = data
    .filter((d) => d.country === selectedCountry)
    .flatMap((d) => d.suites.flatMap((s) => s.platforms.map((p) => ({
      suite: s.suite,
      platform: p.platform,
      date: d.date,
      tests: p.tests,
    }))))
    .filter((entry) => entry.platform === selectedDevice)
    .flatMap((entry) =>
      entry.tests.map((t) => ({
        suite: entry.suite,
        testName: t.testName,
        status: t.status,
        duration: t.duration / 1000,
        error: t.error,
        annotations: t.annotations,
        date: entry.date,
      })));

  const getCountryData = (suiteName: string, date: string): ChartItem[] => {
    const relevantReports = data.filter((d) => d.date === date);
    const countryStatus: Record<
      string,
      { total: number; failed: boolean; skipped: number; passed: number }
    > = {};

    relevantReports.forEach((report) => {
      report.suites
        .filter((s) => s.suite === suiteName)
        .forEach((suite) => {
          suite.platforms.forEach((p) => {
            p.tests.forEach((t) => {
              const key = report.country;
              if (!countryStatus[key]) {
                countryStatus[key] = { total: 0, failed: false, skipped: 0, passed: 0 };
              }
              countryStatus[key].total++;
              if (t.status === 'failed') countryStatus[key].failed = true;
              if (t.status === 'skipped') countryStatus[key].skipped++;
              if (t.status === 'passed') countryStatus[key].passed++;
            });
          });
        });
    });

    return Object.entries(countryStatus).map(([country, stat]) => {
      let fill = '#00C49F';
      if (stat.failed) fill = '#FF4C4C';
      else if (stat.skipped === stat.total) fill = '#FFBB28';
      return { name: country, value: stat.total, fill };
    });
  };


  // const getCountryData = (suiteName: string, date: string): ChartItem[] => {
  //   const relevantReports = data.filter((d) => d.date === date);
  //   const countryStatus: Record<
  //     string,
  //     { total: number; failed: boolean; skipped: number; passed: number }
  //   > = {};

  //   relevantReports.forEach((report) => {
  //     const suite = report.suites.find((s) => s.suite === suiteName);
  //     if (!suite) return;

  //     suite.platforms.forEach((p) => {
  //       p.tests.forEach((t) => {
  //         const key = report.country;
  //         if (!countryStatus[key]) {
  //           countryStatus[key] = { total: 0, failed: false, skipped: 0, passed: 0 };
  //         }
  //         countryStatus[key].total++;
  //         if (t.status === 'failed') countryStatus[key].failed = true;
  //         if (t.status === 'skipped') countryStatus[key].skipped++;
  //         if (t.status === 'passed') countryStatus[key].passed++;
  //       });
  //     });
  //   });

  //   return Object.entries(countryStatus).map(([country, stat]) => {
  //     let fill = '#00C49F';
  //     if (stat.failed) fill = '#FF4C4C';
  //     else if (stat.skipped === stat.total) fill = '#FFBB28';
  //     return { name: country, value: stat.total, fill };
  //   });
  // };

  const getPlatformData = (suite: string, country: string, date: string) => {
    const r = data.find((d) => d.date === date && d.country === country);
    if (!r) return [];
    const s = r.suites.find((s) => s.suite === suite);
    if (!s) return [];

    return s.platforms.map((p) => {
      const failed = p.tests.some((t) => t.status === 'failed');
      const skipped = p.tests.every((t) => t.status === 'skipped');
      return {
        name: p.platform,
        value: p.tests.length,
        fill: failed ? '#FF4C4C' : skipped ? '#FFBB28' : '#00C49F'
      };
    });
  };

  const renderTestCards = (
    expandedCard: number | null,
    setExpandedCard: React.Dispatch<React.SetStateAction<number | null>>
  ) => {
    const tests = data
      .filter((d) => d.date === drilledDate && d.country === drilledCountry)
      .flatMap((d) => d.suites)
      .filter((s) => s.suite === drilledSuite)
      .flatMap((s) => s.platforms)
      .filter((p) => p.platform === drilledPlatform)
      .flatMap((p) => p.tests);

    return (
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          Test Details: {drilledSuite} / {drilledCountry} / {drilledPlatform} / {drilledDate}
        </Typography>

        {tests.map((t, i) => {
          const isExpanded = expandedCard === i;
          return (
            <Card key={i} sx={{ my: 2, bgcolor: '#2a2a2a' }}>
              <CardContent onClick={() => setExpandedCard(isExpanded ? null : i)} sx={{ cursor: 'pointer' }}>
                <Typography variant="subtitle1" sx={{ color: getColor(t.status) }}>
                  {t.testName} ({t.status})
                </Typography>
                {t.duration && (
                  <Typography variant="body2">Duration: {t.duration}ms</Typography>
                )}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box sx={{ mt: 2 }}>
                    {t.error?.message && (
                      <Typography sx={{ mt: 1, color: '#f28b82', whiteSpace: 'pre-wrap' }}>
                        Error: {t.error.message.replace(/\x1B\[[0-9;]*m/g, '')}
                      </Typography>
                    )}
                    {t.error?.pathToScreenshot && (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenImage(`${API_URL}${t.error?.pathToScreenshot}`);
                        }}
                        sx={{ mt: 1 }}
                      >
                        View Screenshot
                      </Button>
                    )}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  };


  const renderFilter = () => (
    <Box
      sx={{
        display: 'flex',
        ...(isMobile && {
          flexDirection: 'column',
          alignItems: 'flex-start',
          flexWrap: 'wrap'
        }),
        gap: 2,
        mb: 4,
      }}
    >
      <FormControl fullWidth={isMobile} sx={{ minWidth: 200 }}>
        <InputLabel sx={{ color: '#f1f1f1' }}>Filter by Country</InputLabel>
        <Select
          value={selectedCountry}
          onChange={(e) => setSelectedCountry(e.target.value)}
          label="Filter by Country"
        >
          {allCountries.map((c) => (
            <MenuItem key={c} value={c}>
              {c}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth={isMobile} sx={{ minWidth: 200 }}>
        <InputLabel sx={{ color: '#f1f1f1' }}>Filter by Device</InputLabel>
        <Select
          value={selectedDevice}
          onChange={(e) => setSelectedDevice(e.target.value)}
          label="Filter by Device"
        >
          {allDevices.map((d) => (
            <MenuItem key={d} value={d}>
              {d}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        variant="contained"
        onClick={() => setFilterActive(true)}
        disabled={!selectedCountry || !selectedDevice}
        sx={{
          ...(isMobile
            ? { alignSelf: 'stretch', width: '100%' }
            : { alignSelf: 'center', width: 'auto', ml: 10, }),
        }}
      >
        Filter
      </Button>

      <Box sx={{
        display: 'flex',
        mt: 2,
        ...(isMobile && {
          flexDirection: 'column',
          flex: 1,
          gap: 1,
        }),
        ...(!isMobile && {
          gap: 4,
          mb: 4,
          ml: 24,
        }),
      }}>
        <Typography variant="body1">Legend:</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#00C49F', borderRadius: '50%' }} />
            <Typography>Passed (all)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#FFBB28', borderRadius: '50%' }} />
            <Typography>Skipped (all)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: '#FF4C4C', borderRadius: '50%' }} />
            <Typography>Failed (at least one)</Typography>
          </Box>
        </Box>
      </Box>
      <CleanupModal />
      {drilledSuite && drilledCountry && (
        <Button variant="outlined" onClick={handleBack} sx={{ mb: 2, ml: 2 }}>
          Back
        </Button>
      )}
      {filterActive && (
        <Button
          variant="outlined"
          onClick={() => setFilterActive(false)}
          sx={isMobile ? { mt: 2 } : { mb: 2, ml: 2 }}
        >
          Back to Chart
        </Button>
      )}
    </Box>
  );

  const renderActiveShape = (props: any) => {
    const {
      cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle,
      fill, payload, percent, name
    } = props;
    const RADIAN = Math.PI / 180;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 30) * cos;
    const my = cy + (outerRadius + 30) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 22;
    const ey = my;
    const textAnchor = cos >= 0 ? 'start' : 'end';

    return (
      <g>
        <text x={cx} y={cy} dy={8} textAnchor="middle" fill="#fff">
          {name}
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 6}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />
        <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
        <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} textAnchor={textAnchor} fill="#fff">{`${(percent * 100).toFixed(1)}%`}</text>
      </g>
    );
  };


  const renderContent = () => {
    if (renderingCharts) {
      return (
        <Box sx={{ minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress color="inherit" />
        </Box>
      );
    }

    if (filterActive) {
      return (
        <TableContainer component={Paper}
          sx={{
            bgcolor: '#1a1d24',
            ...(isMobile && {
              overflowX: 'auto',
              maxWidth: '100%'
            })
          }
          }>
          <Table sx={isMobile ? {} : { minWidth: 600 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: '#f1f1f1' }}>Suite</TableCell>
                <TableCell sx={{ color: '#f1f1f1', wordBreak: 'break-word' }}>Test Name</TableCell>
                {uniqueDates.map((date) => (
                  <TableCell key={date} sx={{ color: '#f1f1f1', wordBreak: 'break-word' }}>{new Date(date).toLocaleDateString()}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(filteredTests.reduce((acc, { suite, testName, date, status, duration, error, annotations }) => {
                if (!acc[suite]) acc[suite] = {};
                if (!acc[suite][testName]) acc[suite][testName] = {};
                acc[suite][testName][date] = { status, duration, error, annotations };
                return acc;
              }, {} as Record<string, Record<string, Record<string, { status: string; duration: number; error?: any; annotations?: Record<string, string>; }>>>)).flatMap(([suite, tests]) =>
                Object.entries(tests).map(([testName, dates], idx) => {
                  const testKey = suite + testName;
                  const isExpanded = expandedTest === testKey;
                  return (
                    <React.Fragment key={testKey + idx}>
                      <TableRow>
                        <TableCell sx={{ color: '#f1f1f1' }}>{suite}</TableCell>
                        <TableCell sx={{ wordBreak: 'break-word' }}>
                          <a onClick={() => setExpandedTest(isExpanded ? null : testKey)} style={{ color: '#90caf9', cursor: 'pointer' }}>{testName}</a>
                        </TableCell>
                        {uniqueDates.map((date) => (
                          <TableCell key={date} sx={{ color: getColor(dates[date]?.status) }}>{dates[date]?.status || '-'}</TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={5} sx={{ p: 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Grow in={isExpanded} timeout={400}>
                              <Card sx={{ bgcolor: '#2a2a2a', m: isMobile ? 0 : 1, p: isMobile ? 1 : 2, borderRadius: isMobile ? 0 : 2, boxShadow: isMobile ? 'none' : 3 }}>
                                <CardContent sx={{ p: isMobile ? 1 : 2 }}>
                                  <Typography variant="h6">{testName}</Typography>
                                  {uniqueDates.map((date) => dates[date] && (
                                    <Box key={date} sx={{ mt: 1 }}>
                                      <Typography>Status on {new Date(date).toLocaleDateString()}: {dates[date].status === 'passed' ? '✅ Passed' : dates[date].status === 'failed' ? '❌ Failed' : '⚠️ Skipped'}</Typography>
                                      <Typography>Duration: {dates[date].duration.toFixed(2)} sec</Typography>
                                      {dates[date].status === 'failed' && dates[date].error?.message && (
                                        <Typography sx={{ mt: isMobile ? 2 : 1, fontSize: isMobile ? '0.95rem' : '1rem', color: '#f28b82', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                                          Error: {dates[date].error.message.replace(/\u001b\[[0-9;]*m/g, '').trim()}
                                        </Typography>
                                      )}
                                      {dates[date].error?.pathToScreenshot && (
                                        <Box sx={{ mt: 2 }}>
                                          <Typography variant="subtitle1">📸 Screenshot:</Typography>
                                          <img src={`${API_URL}${dates[date].error.pathToScreenshot}`} alt="Failure screenshot" style={{ marginTop: '0.5rem', borderRadius: '8px', maxWidth: '100%', maxHeight: '200px', border: '1px solid #ccc', cursor: 'pointer' }} onClick={() => setOpenImage(`${API_URL}${dates[date].error.pathToScreenshot}`)} />
                                        </Box>
                                      )}
                                      {Object.entries(dates[date].annotations ?? {}).map(([key, val], i) => (
                                        <Typography sx={{ mt: 1, color: 'antiquewhite' }} key={i}>{key}: {val}</Typography>
                                      ))}
                                    </Box>
                                  ))}
                                </CardContent>
                              </Card>
                            </Grow>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    if (drilledSuite && drilledCountry && drilledPlatform && drilledDate) return renderTestCards(expandedCard, setExpandedCard);
    if (drilledSuite && drilledCountry && drilledDate) {
      const pieData = getPlatformData(drilledSuite, drilledCountry, drilledDate);
      return (
        <ResponsiveContainer width="100%" height={isMobile ? 300 : 400}>
          <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              activeShape={renderActiveShape}
              outerRadius={isMobile ? 80 : 140}
              style={{ cursor: 'pointer' }}
              isAnimationActive={false}
              label={({
                cx,
                cy,
                midAngle,
                innerRadius,
                outerRadius,
                percent,
                name,
              }) => {
                const RADIAN = Math.PI / 180;
                const radius = innerRadius + (outerRadius - innerRadius) * 0.6;
                const x = cx + radius * Math.cos(-midAngle * RADIAN);
                const y = cy + radius * Math.sin(-midAngle * RADIAN);
                if (percent < 0.02) return null;
                return (
                  <text
                    x={x}
                    y={y}
                    fill="#fff"
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={percent < 0.05 ? 10 : 12}
                  >
                    {name}
                  </text>
                );
              }}

              // label={({
              //   cx,
              //   cy,
              //   midAngle,
              //   innerRadius,
              //   outerRadius,
              //   percent,
              //   name,
              // }) => {
              //   const RADIAN = Math.PI / 180;
              //   const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
              //   const x = cx + radius * Math.cos(-midAngle * RADIAN);
              //   const y = cy + radius * Math.sin(-midAngle * RADIAN);
              //   return (
              //     <text
              //       x={x}
              //       y={y}
              //       fill="#fff"
              //       textAnchor="middle"
              //       dominantBaseline="central"
              //       fontSize={percent < 0.05 ? 10 : 12}
              //     >
              //       {name}
              //     </text>
              //   );
              // }}
              onClick={(entry) => {
                setRenderingCharts(true);
                setDrilledPlatform(entry.name);
              }}

            >
              {pieData.map((entry, i) => <Cell key={i} fill={entry.fill} style={{ cursor: 'pointer' }} className="pie-slice" />)}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      );
    }
    return (
      <Box>
        <TableContainer component={Paper} sx={
          {
            bgcolor: '#1a1d24',
            ...(isMobile && {
              overflowX: 'auto',
              maxWidth: '100%',
            }),
          }}>
          <Table sx={isMobile ? {} : { minWidth: 600 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: '#f1f1f1', fontWeight: 'bold' }}>Suite</TableCell>
                {uniqueDates.map((date) => (
                  <TableCell key={date} sx={{ color: '#f1f1f1', fontWeight: 'bold' }}>
                    {new Date(date).toLocaleDateString()}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {suiteNames.map((suite) => (
                <TableRow key={suite}>
                  <TableCell sx={{ color: '#f1f1f1' }}>{suite}</TableCell>
                  {uniqueDates.map((date) => (
                    <TableCell key={date}>
                      <ResponsiveContainer
                        width={isMobile ? '100%' : 350}
                        height={isMobile ? 300 : 350}
                      >
                        <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                          <Pie
                            data={getCountryData(suite, date)}
                            dataKey="value"
                            nameKey="name"
                            activeShape={renderActiveShape}
                            outerRadius={isMobile ? '70%' : 120}
                            labelLine={false}
                            style={{ cursor: 'pointer' }}
                            isAnimationActive={false}
                            label={({
                              cx,
                              cy,
                              midAngle,
                              innerRadius,
                              outerRadius,
                              percent,
                              name,
                            }) => {
                              const RADIAN = Math.PI / 180;
                              const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                              const x = cx + radius * Math.cos(-midAngle * RADIAN);
                              const y = cy + radius * Math.sin(-midAngle * RADIAN);
                              return (
                                <text
                                  x={x}
                                  y={y}
                                  fill="#fff"
                                  textAnchor="middle"
                                  dominantBaseline="central"
                                  fontSize={percent < 0.05 ? 10 : 12}
                                >
                                  {name}
                                </text>
                              );
                            }}
                            onClick={(entry) => {
                              setRenderingCharts(true);
                              setDrilledSuite(suite);
                              setDrilledCountry(entry.name);
                              setDrilledDate(date);
                            }}

                          >
                            {getCountryData(suite, date).map((entry, i) => <Cell key={i} fill={entry.fill} style={{ cursor: 'pointer' }} className="pie-slice" />)}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const handleBack = () => {
    if (drilledPlatform) setDrilledPlatform(null);
    else if (drilledCountry) setDrilledCountry(null);
    else if (drilledSuite) setDrilledSuite(null);
  };

  return (
    <Box sx={{ p: 6, bgcolor: '#0f1117', color: '#f1f1f1' }}>
      <Typography variant="h4" gutterBottom>
        Test Suites Overview (Past 3 Days)
      </Typography>
      {renderFilter()}
      {filterActive ? (
        <Typography sx={{ mb: 2, textAlign: 'center', fontWeight: 'bold' }}>
          Filtered results for {selectedCountry} / {selectedDevice}
        </Typography>
      ) : null}
      {renderContent()}
      <Dialog open={!!openImage} onClose={() => setOpenImage(null)} maxWidth="xl">
        <DialogContent sx={{ p: 0, bgcolor: 'black' }}>
          <img src={openImage ?? ''} alt="Full screenshot" style={{ width: '100%', height: 'auto' }} onClick={() => setOpenImage(openImage ?? '')} />
        </DialogContent>
      </Dialog>
    </Box>
  )
}

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: { default: '#0f1117', paper: '#1a1d24' },
    text: { primary: '#f1f1f1' },
  },
});

const WrappedDashboard = () => (
  <ThemeProvider theme={darkTheme}>
    <CssBaseline />
    <Dashboard />
  </ThemeProvider>
);

export default WrappedDashboard;

